import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout/AppLayout';
import { Button, IconButton } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/ToastProvider';
import { FilterIcon, PlusIcon } from '../../components/icons/Icons';
import { api, errorMessage } from '../../lib/api';
import { useAsync, useDebounced } from '../../lib/hooks';
import { RFP_STATUS_META } from '../../lib/status';
import type { Paginated, RfpListItem, RfpStats, RfpStatus } from '../../types/api';
import { RfpTable } from './components/RfpTable';

const PER_PAGE_OPTIONS = [10, 25, 50].map((value) => ({ value, label: String(value) }));

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  ...(Object.keys(RFP_STATUS_META) as RfpStatus[]).map((value) => ({
    value,
    label: RFP_STATUS_META[value].label,
  })),
];

/**
 * RFP 01 - the list screen the Figma file opens with.
 * Toolbar ("All RFPs (n)", search, filter, Add RFP), the aggregated table and
 * the "Per Page / 1-5 of 5" footer.
 */
export function RfpListPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<RfpStatus | 'all'>('all');
  const [deleteTarget, setDeleteTarget] = useState<RfpListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const debouncedSearch = useDebounced(search, 300);
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const loader = useCallback(
    () =>
      api.get<Paginated<RfpListItem>>('/rfps', {
        page,
        perPage,
        search: debouncedSearch || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sortBy: 'created_on',
        sortOrder: 'desc',
      }),
    [page, perPage, debouncedSearch, statusFilter],
  );

  const { data, loading, error, reload } = useAsync(loader, [
    page,
    perPage,
    debouncedSearch,
    statusFilter,
  ]);

  const statsLoader = useCallback(() => api.get<RfpStats>('/rfps/stats'), []);
  const { data: stats, reload: reloadStats } = useAsync(statsLoader, []);

  const incomingToast = (location.state as { toast?: string } | null)?.toast;
  // React StrictMode runs mount effects twice in dev; the ref keeps the handed
  // over toast to exactly one notification.
  const shownToastRef = useRef<string | null>(null);

  // The wizard hands the success toast over through router state so it shows on
  // this screen, exactly like the "RFP created successfully ..." design frame.
  useEffect(() => {
    if (!incomingToast) return;
    if (shownToastRef.current === incomingToast) return;
    shownToastRef.current = incomingToast;
    showToast(incomingToast, 'success');
    navigate(location.pathname, { replace: true, state: null });
    reloadStats();
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingToast]);

  // A changed filter or page size makes the current page number meaningless.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, perPage]);

  const items = data?.items ?? [];
  const pagination = data?.pagination;
  const totalForTitle = stats?.total ?? pagination?.total ?? 0;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/rfps/${deleteTarget.id}`);
      showToast(`${deleteTarget.rfpNumber} was deleted.`);
      setDeleteTarget(null);
      reload();
      reloadStats();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const emptyMessage = useMemo(() => {
    if (debouncedSearch) return `No RFPs match "${debouncedSearch}".`;
    if (statusFilter !== 'all') {
      return `No RFPs are currently ${RFP_STATUS_META[statusFilter].label}.`;
    }
    return 'No RFPs yet. Use "Add RFP" to create one.';
  }, [debouncedSearch, statusFilter]);

  return (
    <AppLayout title="RFP">
      <div className="page-header">
        <div className="toolbar">
          <span className="toolbar__title">All RFPs ({totalForTitle})</span>
          <SearchInput
            placeholder="Search..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search RFPs"
          />
          <Select
            aria-label="Filter by status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as RfpStatus | 'all')}
            wrapClassName="toolbar__select"
          />
          <IconButton label="More filters" brand>
            <FilterIcon size={18} />
          </IconButton>
        </div>

        <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => navigate('/rfp/new')}>
          Add RFP
        </Button>
      </div>

      <section className="panel panel--flush">
        <RfpTable
          items={items}
          loading={loading}
          error={error}
          emptyMessage={emptyMessage}
          onRetry={reload}
          onEdit={(rfp) => navigate(`/rfp/${rfp.id}?edit=1`)}
          onDelete={(rfp) => setDeleteTarget(rfp)}
        />
      </section>

      <div className="table-footer">
        <Pagination
          page={pagination?.page ?? 1}
          totalPages={pagination?.totalPages ?? 1}
          onChange={setPage}
        />

        <div className="table-footer__per-page">
          <span className="table-footer__per-page-label">Per Page</span>
          <Select
            aria-label="Rows per page"
            options={PER_PAGE_OPTIONS}
            value={perPage}
            onChange={(event) => setPerPage(Number(event.target.value))}
            className="table-footer__select"
          />
        </div>

        <span className="table-footer__range">{pagination?.rangeLabel ?? '0-0 of 0'}</span>
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete RFP"
        message={
          deleteTarget
            ? `This permanently deletes ${deleteTarget.rfpNumber} together with its vendors, products and documents.`
            : ''
        }
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </AppLayout>
  );
}
